import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';

export default function OTDRTestPage() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const testTypes = [
    {
      title: 'OTDR Ölçümü',
      desc: 'Fiber hattın tüm parametrelerinin detaylı analizi',
      icon: 'ri-line-chart-line',
      features: ['Kayıp Ölçümü', 'Mesafe Tespiti', 'Olay Analizi', 'Grafik Rapor'],
    },
    {
      title: 'Güç Ölçümü',
      desc: 'Fiber hattın optik güç seviyesinin ölçülmesi',
      icon: 'ri-flashlight-line',
      features: ['Işık Kaynağı', 'Güç Ölçer', 'dBm Ölçüm', 'Kayıp Hesaplama'],
    },
    {
      title: 'Arıza Tespiti',
      desc: 'Fiber hattaki arıza noktasının hassas lokalizasyonu',
      icon: 'ri-map-pin-line',
      features: ['Konum Belirleme', 'Arıza Tipi', 'Kayıp Değeri', 'Yansıma Analizi'],
    },
    {
      title: 'Sertifikasyon',
      desc: 'Fiber hattın standartlara uygunluk testleri',
      icon: 'ri-shield-check-line',
      features: ['TIA/EIA Standart', 'ISO/IEC Uyumluluk', 'Test Raporu', 'Garanti Belgesi'],
    },
  ];

  const measurements = [
    { param: 'Toplam Kayıp', unit: 'dB', desc: 'Fiber hattın baştan sona toplam kaybı' },
    { param: 'Ek Kaybı', unit: 'dB', desc: 'Her bir ek noktasındaki kayıp değeri' },
    { param: 'Mesafe', unit: 'km', desc: 'Fiber hattın toplam uzunluğu' },
    { param: 'Yansıma', unit: 'dB', desc: 'Ek noktalarındaki yansıma değerleri' },
    { param: 'ORL', unit: 'dB', desc: 'Optical Return Loss - Toplam yansıma kaybı' },
    { param: 'Dalga Boyu', unit: 'nm', desc: '1310nm, 1550nm test dalga boyları' },
  ];

  const faqData = [
    {
      question: 'OTDR testi nedir ve neden yapılır?',
      answer: 'OTDR (Optical Time Domain Reflectometer) testi, fiber optik hattın kalitesini ölçen ve arıza noktalarını tespit eden bir test yöntemidir. Fiber hatta ışık gönderilir ve geri dönen yansımalar analiz edilir. Bu sayede fiber uzunluğu, kayıp değerleri, ek noktaları ve arıza lokasyonları hassas şekilde belirlenir. Yeni kurulumların kabulü, periyodik bakım ve arıza tespiti için kritik öneme sahiptir.',
    },
    {
      question: 'OTDR testi ne kadar sürer?',
      answer: 'Standart bir OTDR testi, fiber sayısına ve hat uzunluğuna bağlı olarak değişir. Tek bir fiber için test süresi 5-10 dakikadır. 12 fiber için ortalama 1-2 saat, 24 fiber için 2-4 saat sürmektedir. Test sonrası rapor hazırlama ve analiz için ek süre gerekir.',
    },
    {
      question: 'OTDR test raporu neleri içerir?',
      answer: 'OTDR test raporu; fiber hattın grafik gösterimi, toplam kayıp değeri, her bir ek noktasının kaybı, fiber uzunluğu, yansıma değerleri, ORL (Optical Return Loss), test parametreleri ve standartlara uygunluk bilgilerini içerir. Rapor PDF formatında teslim edilir ve arşivlenir.',
    },
    {
      question: 'Hangi dalga boylarında OTDR testi yapılır?',
      answer: 'Singlemode fiber için 1310nm ve 1550nm dalga boylarında test yapılır. Multimode fiber için 850nm ve 1300nm dalga boyları kullanılır. Kritik uygulamalarda her iki dalga boyunda da test yapılması önerilir çünkü farklı dalga boylarında farklı kayıp değerleri görülebilir.',
    },
    {
      question: 'OTDR ile hangi arızalar tespit edilebilir?',
      answer: 'OTDR ile fiber kırılması, yüksek kayıplı ekler, bükülme kayıpları, konnektör sorunları, fiber ezilmesi ve makro bending gibi arızalar tespit edilebilir. Arızanın tipi ve tam lokasyonu (metre cinsinden) belirlenir, bu sayede müdahale çok daha hızlı ve etkili olur.',
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <title>OTDR Test ve Arıza Tespiti İstanbul | Fiber Optik Ölçüm ve Sertifikasyon</title>
      <meta name="description" content="İstanbul ve çevre illerde profesyonel OTDR test hizmeti. Fiber optik hat ölçümü, arıza tespiti, güç ölçümü ve sertifikasyon. Detaylı test raporu ve analiz." />
      <meta name="keywords" content="OTDR test, fiber test, fiber ölçüm, arıza tespiti, fiber sertifikasyon, güç ölçümü, İstanbul OTDR, fiber hat testi, optik test" />
      <link rel="canonical" href="https://fiberek.com/fiber-hizmetleri/otdr-test" />
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      <div className="min-h-screen bg-dark-950">
        <Navbar />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/20 via-dark-950 to-violet-900/20"></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                  OTDR Test ve
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-violet-400">
                    Arıza Tespiti
                  </span>
                </h1>

                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Profesyonel OTDR cihazları ile fiber optik hatların detaylı analizi ve arıza noktalarının hassas tespiti. 
                  Güç ölçümü, sertifikasyon ve kapsamlı test raporları.
                </p>

                <div className="flex flex-wrap gap-4 mb-8">
                  <a
                    href="tel:+905537165561"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-all duration-300 font-semibold shadow-lg shadow-indigo-500/30 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line"></i>
                    Hemen Ara
                  </a>
                  <a
                    href="https://wa.me/905537165561"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all duration-300 font-semibold shadow-lg shadow-emerald-500/30 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line"></i>
                    WhatsApp
                  </a>
                </div>

                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <div className="text-3xl font-bold text-indigo-400 mb-1">±1m</div>
                    <div className="text-sm text-gray-400">Hassasiyet</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-violet-400 mb-1">100km</div>
                    <div className="text-sm text-gray-400">Test Menzili</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-purple-400 mb-1">0.01dB</div>
                    <div className="text-sm text-gray-400">Kayıp Hassasiyeti</div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <div className="relative w-full h-96 rounded-2xl overflow-hidden">
                  <img
                    src="https://public.readdy.ai/ai/img_res/7a2266166eb7355004f223d3b34919cd.jpg"
                    alt="OTDR Test"
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Test Types */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Test Hizmetlerimiz</h2>
              <p className="text-gray-400">Fiber optik hatlar için kapsamlı test ve ölçüm çözümleri</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {testTypes.map((test, index) => (
                <motion.div
                  key={test.title}
                  ref={ref}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-6 border border-white/10 hover:border-indigo-500/50 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-indigo-500/20 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${test.icon} text-2xl text-indigo-400`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{test.title}</h3>
                  <p className="text-gray-400 text-sm mb-4">{test.desc}</p>
                  <ul className="space-y-1">
                    {test.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-xs text-gray-300">
                        <i className="ri-check-line text-indigo-400"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-20 bg-gradient-to-br from-indigo-900/10 via-dark-950 to-violet-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16">
              <div>
                <h2 className="text-4xl font-bold text-white mb-6">
                  OTDR Testi Nedir?
                </h2>
                <div className="prose prose-invert max-w-none">
                  <p className="text-gray-300 leading-relaxed mb-4">
                    <strong className="text-white">OTDR (Optical Time Domain Reflectometer)</strong>, fiber optik kabloların kalitesini ölçen 
                    ve arıza noktalarını tespit eden gelişmiş bir test cihazıdır. Çalışma prensibi, fiber hatta kısa süreli ışık darbeleri 
                    göndermek ve geri dönen yansımaları analiz etmek üzerine kuruludur.
                  </p>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    OTDR cihazı, fiber hattaki her olay (ek, konnektör, bükülme, kırılma) için bir yansıma sinyali alır. 
                    Bu sinyallerin zamanlaması ve gücü analiz edilerek, olayın tipi, konumu ve kayıp değeri belirlenir. 
                    Sonuç olarak fiber hattın tamamını gösteren detaylı bir grafik elde edilir.
                  </p>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    OTDR testi, yeni fiber kurulumlarının kabulü, periyodik bakım, arıza tespiti ve fiber hat kalitesinin 
                    dokümantasyonu için vazgeçilmezdir. Test sonuçları PDF rapor olarak saklanır ve gerektiğinde referans olarak kullanılır.
                  </p>
                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10 mt-6">
                    <h3 className="text-xl font-bold text-white mb-3">OTDR Testi Hangi Durumlarda Yapılır?</h3>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-indigo-400 mt-1"></i>
                        <span>Yeni fiber kurulumlarının kabul testleri</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-indigo-400 mt-1"></i>
                        <span>Fiber arıza tespiti ve lokalizasyon</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-indigo-400 mt-1"></i>
                        <span>Periyodik bakım ve kalite kontrolü</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-indigo-400 mt-1"></i>
                        <span>Fiber ek kalitesinin doğrulanması</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-indigo-400 mt-1"></i>
                        <span>Sertifikasyon ve dokümantasyon</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-4xl font-bold text-white mb-6">
                  OTDR Test Parametreleri
                </h2>
                <div className="space-y-4">
                  {measurements.map((measurement, index) => (
                    <motion.div
                      key={measurement.param}
                      initial={{ opacity: 0, x: 20 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: index * 0.1 }}
                      className="bg-dark-900 rounded-xl p-6 border border-white/10"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-bold text-white">{measurement.param}</h3>
                        <span className="text-indigo-400 font-mono text-sm">{measurement.unit}</span>
                      </div>
                      <p className="text-gray-400 text-sm">{measurement.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Technical Details */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">
                OTDR Test Süreci
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-8 border border-white/10"
              >
                <div className="w-12 h-12 bg-indigo-500/20 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-indigo-400">1</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Hazırlık</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Fiber uçların temizlenmesi, OTDR cihazının kalibrasyonu ve test parametrelerinin ayarlanması. 
                  Launch cable ve tail cable bağlantısı.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-8 border border-white/10"
              >
                <div className="w-12 h-12 bg-violet-500/20 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-violet-400">2</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Test</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Her fiber için 1310nm ve 1550nm dalga boylarında OTDR testi yapılır. 
                  Cihaz otomatik olarak ölçüm yapar ve grafik oluşturur.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-8 border border-white/10"
              >
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-purple-400">3</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Analiz</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  OTDR grafiği detaylı olarak analiz edilir. Ek noktaları, kayıp değerleri, 
                  yansımalar ve olası arızalar belirlenir.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-8 border border-white/10"
              >
                <div className="w-12 h-12 bg-indigo-500/20 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-indigo-400">4</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Güç Ölçümü</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Işık kaynağı ve güç ölçer ile fiber hattın optik güç seviyesi ölçülür. 
                  Toplam kayıp değeri doğrulanır.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-8 border border-white/10"
              >
                <div className="w-12 h-12 bg-violet-500/20 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-violet-400">5</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Raporlama</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Detaylı test raporu hazırlanır. OTDR grafikleri, ölçüm sonuçları, 
                  standartlara uygunluk bilgileri raporda yer alır.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-8 border border-white/10"
              >
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-purple-400">6</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Teslimat</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Test raporu PDF formatında teslim edilir ve dijital arşivde saklanır. 
                  Gerektiğinde tekrar erişim sağlanabilir.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Equipment Section */}
        <section className="py-20 bg-gradient-to-br from-indigo-900/10 via-dark-950 to-violet-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16">
              <div>
                <h2 className="text-4xl font-bold text-white mb-6">
                  Kullandığımız Ekipmanlar
                </h2>
                <div className="space-y-6">
                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-3">OTDR Cihazları</h3>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-indigo-400 mt-1"></i>
                        <span>Fluke OptiFiber Pro - Yüksek hassasiyet, otomatik analiz</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-indigo-400 mt-1"></i>
                        <span>EXFO MaxTester - Kompakt, saha tipi OTDR</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-indigo-400 mt-1"></i>
                        <span>Anritsu MT9083 - Uzun mesafe, yüksek dinamik aralık</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-3">Güç Ölçüm Ekipmanları</h3>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-indigo-400 mt-1"></i>
                        <span>Işık Kaynağı: 850/1300/1310/1550nm</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-indigo-400 mt-1"></i>
                        <span>Optik Güç Ölçer: ±0.01dB hassasiyet</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-indigo-400 mt-1"></i>
                        <span>VFL (Visual Fault Locator): Kısa mesafe arıza tespiti</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-4xl font-bold text-white mb-6">
                  Test Standartları
                </h2>
                <div className="space-y-6">
                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-3">TIA/EIA Standartları</h3>
                    <p className="text-gray-400 mb-4">
                      Tüm testlerimiz TIA/EIA-568 standartlarına uygun olarak gerçekleştirilir.
                    </p>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-indigo-400 mt-1"></i>
                        <span>TIA-568.3-D: Fiber optik kablolama standartları</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-indigo-400 mt-1"></i>
                        <span>TIA-526-14: OTDR test prosedürleri</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-indigo-400 mt-1"></i>
                        <span>TIA-526-7: Optik güç ölçüm yöntemleri</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-3">ISO/IEC Standartları</h3>
                    <p className="text-gray-400 mb-4">
                      Uluslararası standartlara tam uyumluluk sağlanır.
                    </p>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-indigo-400 mt-1"></i>
                        <span>ISO/IEC 11801: Genel kablolama standartları</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-indigo-400 mt-1"></i>
                        <span>ISO/IEC 14763-3: Fiber optik test yöntemleri</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-indigo-400 mt-1"></i>
                        <span>IEC 61280-4-1: OTDR ölçüm prosedürleri</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-3">Kabul Kriterleri</h3>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-indigo-400 mt-1"></i>
                        <span>Singlemode: Maksimum 0.5 dB/km kayıp</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-indigo-400 mt-1"></i>
                        <span>Multimode: Maksimum 3.0 dB/km kayıp</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-indigo-400 mt-1"></i>
                        <span>Ek kaybı: Maksimum 0.3 dB</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-indigo-400 mt-1"></i>
                        <span>Konnektör kaybı: Maksimum 0.75 dB</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Sıkça Sorulan Sorular
              </h2>
            </div>

            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <motion.details
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-dark-900 rounded-xl border border-white/10 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <h3 className="text-lg font-semibold text-white pr-4">{faq.question}</h3>
                    <i className="ri-arrow-down-s-line text-2xl text-indigo-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-6 pb-6">
                    <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                  </div>
                </motion.details>
              ))}
            </div>
          </div>
        </section>

        {/* Blog & Rehberler Section */}
        <section className="py-20 bg-gradient-to-br from-indigo-900/10 via-dark-950 to-violet-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Blog & Rehberler</h2>
              <p className="text-gray-400">Fiber optik teknolojileri hakkında detaylı bilgiler</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl overflow-hidden border border-white/10 hover:border-indigo-500/50 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=OTDR%20fiber%20optic%20testing%20equipment%20display%20showing%20measurement%20results%2C%20professional%20technician%20analyzing%20fiber%20test%20data%2C%20modern%20testing%20laboratory%20with%20technical%20instruments%2C%20detailed%20screen%20showing%20fiber%20trace%20graph&width=400&height=250&seq=otdr-blog1&orientation=landscape"
                    alt="OTDR Testi"
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-indigo-500/90 text-white text-xs font-semibold rounded-full">Fiber Optik</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">OTDR Testi Nedir ve Nasıl Yapılır?</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">Fiber optik hatların kalite kontrolü ve arıza tespiti için kullanılan OTDR test yöntemleri ve uygulama aşamaları.</p>
                  <a
                    href="/blog/otdr-testi-nedir"
                    className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors text-sm font-semibold"
                  >
                    Okumaya Devam Et
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </motion.article>

              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl overflow-hidden border border-white/10 hover:border-indigo-500/50 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=fiber%20optic%20cable%20repair%20fusion%20splicing%20process%2C%20technician%20working%20with%20precision%20equipment%2C%20blue%20glowing%20fiber%20cables%2C%20professional%20technical%20environment%20with%20modern%20splicing%20tools&width=400&height=250&seq=otdr-blog2&orientation=landscape"
                    alt="Fiber Kablo Tamiri"
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-indigo-500/90 text-white text-xs font-semibold rounded-full">Fiber Optik</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">Fiber Optik Kablo Tamiri Nasıl Yapılır?</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">Kesilen veya hasar gören fiber optik kabloların fusion splicing yöntemi ile profesyonel onarım teknikleri.</p>
                  <a
                    href="/blog/fiber-optik-kablo-tamiri-nasil-yapilir"
                    className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors text-sm font-semibold"
                  >
                    Okumaya Devam Et
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </motion.article>

              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl overflow-hidden border border-white/10 hover:border-indigo-500/50 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=fusion%20splicing%20fiber%20optic%20process%2C%20close-up%20view%20of%20fiber%20fusion%20splicer%20machine%20in%20operation%2C%20precise%20fiber%20alignment%20and%20welding%2C%20professional%20technical%20environment&width=400&height=250&seq=otdr-blog3&orientation=landscape"
                    alt="Fusion Splicing"
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-indigo-500/90 text-white text-xs font-semibold rounded-full">Fiber Optik</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">Fusion Splicing Nedir? Nasıl Yapılır?</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">Fiber optik kabloların kaynaştırılarak birleştirilmesi işlemi olan fusion splicing tekniği ve uygulama detayları.</p>
                  <a
                    href="/blog/fusion-splicing-nedir"
                    className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors text-sm font-semibold"
                  >
                    Okumaya Devam Et
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </motion.article>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-br from-indigo-900/20 via-dark-950 to-violet-900/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              OTDR Test Hizmeti Alın
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Fiber hatlarınızın kalitesini profesyonel ekipmanlarla test edin
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="tel:+905537165561"
                className="inline-flex items-center gap-2 px-8 py-4 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-all duration-300 font-semibold text-lg shadow-lg shadow-indigo-500/30 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line"></i>
                +90 553 716 55 61
              </a>
              <a
                href="https://wa.me/905537165561"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all duration-300 font-semibold text-lg shadow-lg shadow-emerald-500/30 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line"></i>
                WhatsApp
              </a>
            </div>
          </div>
        </section>

        <Footer />
        <FloatingButtons />
      </div>
    </>
  );
}